import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import './Subscription.css';

const Subscription = () => {
  const { isAuthenticated, isPremium, user, fetchUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  // Check if user is on monthly plan
  const isMonthlySubscriber = isPremium() && user?.subscription?.billingPeriod !== 'yearly';
  const isYearlySubscriber = isPremium() && user?.subscription?.billingPeriod === 'yearly';

  const handleSubscribe = async (interval) => {
    if (!isAuthenticated) {
      // Redirect to register first
      window.location.href = '/register?redirect=subscription';
      return;
    }

    // Don't allow subscribing if already premium
    if (isPremium()) {
      toast.info('You already have an active subscription. Use "Manage Subscription" to make changes.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/subscription/create-checkout', { interval });
      
      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      // Check if they already have subscription
      if (error.response?.data?.hasActiveSubscription) {
        toast.info('You already have an active subscription. Use "Manage Subscription" to make changes.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to start checkout');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const response = await api.post('/subscription/create-portal');
      
      if (response.data.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to open billing portal');
      }
    } catch (error) {
      console.error('Portal error:', error);
      toast.error(error.response?.data?.message || 'Failed to open billing portal');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeToYearly = async () => {
    if (!window.confirm('Upgrade to yearly plan? You will be charged the prorated difference and save $20/year going forward.')) {
      return;
    }

    setUpgrading(true);
    try {
      const response = await api.post('/subscription/upgrade-to-yearly');
      
      if (response.data.success) {
        toast.success('Successfully upgraded to yearly plan!');
        // Refresh user data to reflect new subscription
        if (fetchUser) {
          await fetchUser();
        }
        // Force page reload to update all subscription displays
        window.location.reload();
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error(error.response?.data?.message || 'Failed to upgrade subscription');
    } finally {
      setUpgrading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="subscription-page">
      <div className="container">
        <div className="page-header text-center">
          <h1 className="page-title">Choose Your Plan</h1>
          <p className="page-subtitle">
            Generate documents for free or upgrade for premium features
          </p>
        </div>

        {/* Current Plan Status */}
        {isAuthenticated && (
          <div className="current-plan">
            <div className="current-plan-info">
              <span className="plan-badge">
                {isPremium() 
                  ? `⭐ ${user?.subscription?.billingPeriod === 'yearly' ? 'Yearly' : 'Monthly'} Premium`
                  : '🆓 Free'
                }
              </span>
              <span className="plan-status">
                {isPremium() 
                  ? `Active until ${formatDate(user?.subscription?.endDate)}`
                  : 'You are on the free plan'}
              </span>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="pricing-grid">
          {/* Free Plan */}
          <div className="pricing-card">
            <div className="pricing-header">
              <h2>Free</h2>
              <div className="pricing-price">
                <span className="price">$0</span>
                <span className="period">forever</span>
              </div>
            </div>
            <div className="pricing-body">
              <ul className="pricing-features">
                <li className="included">
                  <span className="check">✓</span>
                  Generate unlimited bills of sale
                </li>
                <li className="included">
                  <span className="check">✓</span>
                  Generate unlimited invoices
                </li>
                <li className="included">
                  <span className="check">✓</span>
                  Download as PDF
                </li>
                <li className="included">
                  <span className="check">✓</span>
                  Professional templates
                </li>
                <li className="excluded">
                  <span className="x">✕</span>
                  Save documents (download only)
                </li>
                <li className="excluded">
                  <span className="x">✕</span>
                  Document history
                </li>
                <li className="excluded">
                  <span className="x">✕</span>
                  Ads displayed
                </li>
              </ul>
            </div>
            <div className="pricing-footer">
              {!isAuthenticated ? (
                <Link to="/bill-of-sale" className="btn btn-secondary btn-block btn-lg">
                  Get Started Free
                </Link>
              ) : isPremium() ? (
                <button className="btn btn-secondary btn-block btn-lg" disabled>
                  Current Plan
                </button>
              ) : (
                <Link to="/bill-of-sale" className="btn btn-secondary btn-block btn-lg">
                  Continue Free
                </Link>
              )}
            </div>
          </div>

          {/* Premium Monthly */}
          <div className="pricing-card featured">
            <div className="featured-badge">Most Popular</div>
            <div className="pricing-header">
              <h2>Premium</h2>
              <div className="pricing-price">
                <span className="price">$4.99</span>
                <span className="period">/month</span>
              </div>
            </div>
            <div className="pricing-body">
              <ul className="pricing-features">
                <li className="included">
                  <span className="check">✓</span>
                  Everything in Free
                </li>
                <li className="included highlight">
                  <span className="check">✓</span>
                  Save unlimited documents
                </li>
                <li className="included highlight">
                  <span className="check">✓</span>
                  Access document history
                </li>
                <li className="included highlight">
                  <span className="check">✓</span>
                  No ads
                </li>
                <li className="included">
                  <span className="check">✓</span>
                  Email notifications
                </li>
                <li className="included">
                  <span className="check">✓</span>
                  Priority support
                </li>
                <li className="coming-soon">
                  <span className="star">⭐</span>
                  Custom branding (coming soon)
                </li>
              </ul>
            </div>
            <div className="pricing-footer">
              {isMonthlySubscriber ? (
                <button 
                  className="btn btn-primary btn-block btn-lg"
                  onClick={handleManageSubscription}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : '✓ Current Plan - Manage'}
                </button>
              ) : isYearlySubscriber ? (
                <button className="btn btn-secondary btn-block btn-lg" disabled>
                  Included in Yearly
                </button>
              ) : (
                <button 
                  className="btn btn-primary btn-block btn-lg"
                  onClick={() => handleSubscribe('monthly')}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Start Premium Monthly'}
                </button>
              )}
            </div>
          </div>

          {/* Premium Yearly */}
          <div className="pricing-card">
            <div className="savings-badge">Save $20</div>
            <div className="pricing-header">
              <h2>Premium Yearly</h2>
              <div className="pricing-price">
                <span className="price">$39.99</span>
                <span className="period">/year</span>
              </div>
              <p className="price-note">Just $3.33/month</p>
            </div>
            <div className="pricing-body">
              <ul className="pricing-features">
                <li className="included">
                  <span className="check">✓</span>
                  Everything in Premium
                </li>
                <li className="included highlight">
                  <span className="check">✓</span>
                  Save $20 per year
                </li>
                <li className="included">
                  <span className="check">✓</span>
                  Annual billing
                </li>
                <li className="included">
                  <span className="check">✓</span>
                  All future features
                </li>
              </ul>
            </div>
            <div className="pricing-footer">
              {isYearlySubscriber ? (
                <button 
                  className="btn btn-success btn-block btn-lg"
                  onClick={handleManageSubscription}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : '✓ Current Plan - Manage'}
                </button>
              ) : isMonthlySubscriber ? (
                <button 
                  className="btn btn-success btn-block btn-lg"
                  onClick={handleUpgradeToYearly}
                  disabled={upgrading}
                >
                  {upgrading ? 'Upgrading...' : '⬆️ Upgrade to Yearly (Save $20)'}
                </button>
              ) : (
                <button 
                  className="btn btn-success btn-block btn-lg"
                  onClick={() => handleSubscribe('yearly')}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Start Premium Yearly'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="faq-section">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Can I use the app for free?</h3>
              <p>
                Yes! You can generate unlimited bills of sale and invoices for free. 
                Documents are downloaded as PDFs but not saved to your account.
              </p>
            </div>
            <div className="faq-item">
              <h3>What do I get with Premium?</h3>
              <p>
                Premium users can save all documents, access their full document history, 
                and enjoy an ad-free experience.
              </p>
            </div>
            <div className="faq-item">
              <h3>Can I cancel anytime?</h3>
              <p>
                Yes, you can cancel your subscription at any time. You'll continue to 
                have access until the end of your billing period.
              </p>
            </div>
            <div className="faq-item">
              <h3>What are these documents for?</h3>
              <p>
                Our documents help you create a clear record of your private sales transactions.
                They're useful for keeping track of what was sold, to whom, and for how much.
              </p>
            </div>
          </div>
        </div>

        {/* Money Back Guarantee */}
        <div className="guarantee">
          <div className="guarantee-icon">🛡️</div>
          <div className="guarantee-content">
            <h3>30-Day Money-Back Guarantee</h3>
            <p>
              Not satisfied? Contact us within 30 days for a full refund. No questions asked.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
